import { useCallback, useEffect, useReducer } from "react";

import { hasPermissionChanges } from "@/components/pieces/roleplays/sharing/permissionTransformers";
import { ShareGroup, ShareUser } from "@/components/ui/sharing/types";
import { VisibilityScope } from "@/graphql";
import {
  DEFAULT_PERMISSION,
  ProcessedPermissions,
  ResourcePermissionGroup,
  ResourcePermissionProgram,
  ResourcePermissionUser,
  SharePermissionState,
} from "@/types/permissions";

// Modal state types
export interface ShareModalState {
  // Selection state
  selectedUsers: ShareUser[];
  selectedGroups: ShareGroup[];
  selectedPermission: SharePermissionState;

  // Notification state
  isNotified: boolean;
  message: string;

  // UI state
  showAllPrograms: boolean;
  hasChanges: boolean;

  // Permission data
  sharedUsers: ResourcePermissionUser[];
  sharedGroups: ResourcePermissionGroup[];
  sharedPrograms: ResourcePermissionProgram[];
  visibilityScope: VisibilityScope;

  // Scenario-specific settings
  autoShareSessions: boolean;
}

type ShareModalAction =
  | { type: "SET_SELECTED_USERS"; payload: ShareModalState["selectedUsers"] }
  | { type: "SET_SELECTED_GROUPS"; payload: ShareModalState["selectedGroups"] }
  | { type: "SET_SELECTED_PERMISSION"; payload: SharePermissionState }
  | {
      type: "SET_NOTIFICATION";
      payload: { isNotified: boolean; message: string };
    }
  | { type: "SET_SHOW_ALL_PROGRAMS"; payload: boolean }
  | { type: "SET_HAS_CHANGES"; payload: boolean }
  | {
      type: "SET_PERMISSIONS_DATA";
      payload: ProcessedPermissions & {
        visibilityScope: VisibilityScope;
        autoShareSessions?: boolean;
      };
    }
  | {
      type: "UPDATE_USER_PERMISSION";
      payload: { userId: string; status: SharePermissionState };
    }
  | {
      type: "UPDATE_GROUP_PERMISSION";
      payload: { groupId: string; status: SharePermissionState };
    }
  | { type: "SET_VISIBILITY_SCOPE"; payload: VisibilityScope }
  | { type: "SET_AUTO_SHARE_SESSIONS"; payload: boolean }
  | { type: "RESET_SELECTION" }
  | {
      type: "RESET_ALL";
      payload?: {
        visibilityScope?: VisibilityScope | null;
        autoShareSessions?: boolean | null;
      };
    };

const initialState: ShareModalState = {
  selectedUsers: [],
  selectedGroups: [],
  selectedPermission: DEFAULT_PERMISSION,
  isNotified: true,
  message: "",
  showAllPrograms: false,
  hasChanges: false,
  sharedUsers: [],
  sharedGroups: [],
  sharedPrograms: [],
  visibilityScope: VisibilityScope.User,
  autoShareSessions: false,
};

function shareModalReducer(
  state: ShareModalState,
  action: ShareModalAction,
): ShareModalState {
  switch (action.type) {
    case "SET_SELECTED_USERS":
      return { ...state, selectedUsers: action.payload };

    case "SET_SELECTED_GROUPS":
      return { ...state, selectedGroups: action.payload };

    case "SET_SELECTED_PERMISSION":
      return { ...state, selectedPermission: action.payload };

    case "SET_NOTIFICATION":
      return {
        ...state,
        isNotified: action.payload.isNotified,
        message: action.payload.message,
      };

    case "SET_SHOW_ALL_PROGRAMS":
      return { ...state, showAllPrograms: action.payload };

    case "SET_HAS_CHANGES":
      return { ...state, hasChanges: action.payload };

    case "SET_PERMISSIONS_DATA":
      return {
        ...state,
        sharedUsers: action.payload.users,
        sharedGroups: action.payload.groups,
        sharedPrograms: action.payload.programs,
        visibilityScope: action.payload.visibilityScope,
        autoShareSessions:
          action.payload.autoShareSessions ?? state.autoShareSessions,
      };

    case "UPDATE_USER_PERMISSION":
      return {
        ...state,
        sharedUsers: state.sharedUsers.map((user) =>
          user.id === action.payload.userId
            ? { ...user, shareStatus: action.payload.status }
            : user,
        ),
      };

    case "UPDATE_GROUP_PERMISSION":
      return {
        ...state,
        sharedGroups: state.sharedGroups.map((group) =>
          group.id === action.payload.groupId
            ? { ...group, shareStatus: action.payload.status }
            : group,
        ),
      };

    case "SET_VISIBILITY_SCOPE":
      return { ...state, visibilityScope: action.payload };

    case "SET_AUTO_SHARE_SESSIONS":
      return { ...state, autoShareSessions: action.payload };

    case "RESET_SELECTION":
      return {
        ...state,
        selectedUsers: [],
        selectedGroups: [],
        isNotified: true,
        message: "",
        selectedPermission: DEFAULT_PERMISSION,
      };

    case "RESET_ALL":
      return { ...initialState, hasChanges: false };

    default:
      return state;
  }
}

interface UseShareModalStateProps<T> {
  resource: T;
}

/**
 * Generic hook to manage share modal state with a reducer
 */
export function useShareModalState<
  T extends {
    visibilityScope?: VisibilityScope | null;
    autoShareSessions?: boolean | null;
  },
>({ resource }: UseShareModalStateProps<T>) {
  const [state, dispatch] = useReducer(shareModalReducer, {
    ...initialState,
    visibilityScope: resource.visibilityScope || VisibilityScope.User,
    autoShareSessions: resource.autoShareSessions || false,
  });

  // Check for changes whenever relevant state updates
  useEffect(() => {
    const permissionChanges = hasPermissionChanges(
      state.sharedUsers,
      state.sharedGroups,
    );

    const scopeChanged =
      state.visibilityScope !==
      (resource?.visibilityScope || VisibilityScope.User);
    const autoShareChanged =
      state.autoShareSessions !== (resource?.autoShareSessions || false);
    const hasNewSelections =
      state.selectedUsers.length > 0 || state.selectedGroups.length > 0;

    const hasChanges =
      permissionChanges || scopeChanged || autoShareChanged || hasNewSelections;

    if (hasChanges !== state.hasChanges) {
      dispatch({ type: "SET_HAS_CHANGES", payload: hasChanges });
    }
  }, [
    state.sharedUsers,
    state.sharedGroups,
    state.visibilityScope,
    state.autoShareSessions,
    state.selectedUsers.length,
    state.selectedGroups.length,
    state.hasChanges,
    resource?.visibilityScope,
    resource?.autoShareSessions,
  ]);

  // Helper functions with dispatch wrapped
  const setSelectedUsers = useCallback(
    (users: ShareModalState["selectedUsers"]) => {
      dispatch({ type: "SET_SELECTED_USERS", payload: users });
    },
    [],
  );

  const setSelectedGroups = useCallback(
    (groups: ShareModalState["selectedGroups"]) => {
      dispatch({ type: "SET_SELECTED_GROUPS", payload: groups });
    },
    [],
  );

  const setSelectedPermission = useCallback(
    (permission: ShareModalState["selectedPermission"]) => {
      dispatch({ type: "SET_SELECTED_PERMISSION", payload: permission });
    },
    [],
  );

  const setNotification = useCallback(
    (isNotified: boolean, message: string) => {
      dispatch({ type: "SET_NOTIFICATION", payload: { isNotified, message } });
    },
    [],
  );

  const setAutoShareSessions = useCallback((autoShare: boolean) => {
    dispatch({ type: "SET_AUTO_SHARE_SESSIONS", payload: autoShare });
  }, []);

  const resetAll = useCallback(() => {
    dispatch({ type: "RESET_ALL" });
  }, []);

  return {
    state,
    dispatch,
    // Convenience methods
    setSelectedUsers,
    setSelectedGroups,
    setSelectedPermission,
    setNotification,
    setAutoShareSessions,
    resetAll,
  };
}
